/**
 * Meta Tag Generator Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { IGeneratorTool } from '@/interfaces/ITool';

function tag(name: string, content: string): string {
  const safe = String(content ?? '').replace(/"/g, '&quot;');
  return `<meta name="${name}" content="${safe}">`;
}

function propertyTag(property: string, content: string): string {
  const safe = String(content ?? '').replace(/"/g, '&quot;');
  return `<meta property="${property}" content="${safe}">`;
}

export const MetaTagGeneratorTool: IGeneratorTool = {
  id: 'meta-tag-generator',
  name: 'Meta Tag Generator',
  description: 'Generate common HTML/OG/Twitter meta tags',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['meta', 'og', 'twitter', 'seo', 'html', 'generator'],

  outputType: SEMANTIC_TYPES.HTML,

  attributes: [
    { name: 'title', type: 'string', label: 'Title', defaultValue: '' },
    { name: 'description', type: 'string', label: 'Description', defaultValue: '' },
    { name: 'url', type: 'string', label: 'URL', defaultValue: '' },
    { name: 'image', type: 'string', label: 'Image URL', defaultValue: '' },
    {
      name: 'ogType',
      type: 'select',
      label: 'OG Type',
      defaultValue: 'website',
      options: ['website', 'article', 'profile'],
    },
    {
      name: 'twitterCard',
      type: 'select',
      label: 'Twitter Card',
      defaultValue: 'summary_large_image',
      options: ['summary', 'summary_large_image'],
    },
    { name: 'themeColor', type: 'string', label: 'Theme Color', defaultValue: '' },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const title = String(config.title ?? '').trim();
    const description = String(config.description ?? '').trim();
    const url = String(config.url ?? '').trim();
    const image = String(config.image ?? '').trim();
    const ogType = String(config.ogType ?? 'website').trim() || 'website';
    const twitterCard = String(config.twitterCard ?? 'summary_large_image').trim() || 'summary_large_image';
    const themeColor = String(config.themeColor ?? '').trim();

    const lines: string[] = [];
    if (title) lines.push(`<title>${title}</title>`);
    if (description) lines.push(tag('description', description));
    if (themeColor) lines.push(tag('theme-color', themeColor));

    // OpenGraph
    if (ogType) lines.push(propertyTag('og:type', ogType));
    if (title) lines.push(propertyTag('og:title', title));
    if (description) lines.push(propertyTag('og:description', description));
    if (url) lines.push(propertyTag('og:url', url));
    if (image) lines.push(propertyTag('og:image', image));

    // Twitter
    if (twitterCard) lines.push(tag('twitter:card', twitterCard));
    if (title) lines.push(tag('twitter:title', title));
    if (description) lines.push(tag('twitter:description', description));
    if (image) lines.push(tag('twitter:image', image));

    return lines.join('\n');
  },
};
